import type { SupabaseClient, WebsiteInterfaceLocale } from '@dukanti/shared';
import type { RegionEditor } from '../hooks/use-region-editor';
import { ctaLabelSlotIdOf, labelResolvesEmpty } from '../lib/slot-values';
import { SlotDestinationField } from './fields/slot-destination-field';
import { SlotRichTextField } from './fields/slot-rich-text-field';
import { SlotTextField } from './fields/slot-text-field';

export interface RegionTextFormProps {
  readonly editor: RegionEditor;
  readonly storeId: string;
  readonly interfaceLocale: WebsiteInterfaceLocale;
  /** Epic #228 P5: the destination editor's pickers and readiness run on these. */
  readonly client: SupabaseClient;
  readonly templateId: string;
  readonly supabaseUrl: string;
  /** Epic #228 P5 (UI/UX P2-2): the merchant tried to close with an unfinished field. */
  readonly finishRequested?: boolean;
}

/**
 * The fields of a text or rich-text region: exactly the slots the template
 * declares for this item, in manifest order, and nothing else. A button label
 * or a destination appears only where the template made it editable.
 */
export function RegionTextForm({
  editor,
  storeId,
  interfaceLocale,
  client,
  templateId,
  supabaseUrl,
  finishRequested = false,
}: RegionTextFormProps): React.JSX.Element {
  return (
    <div className="space-y-5">
      {editor.slots.map((slot) => {
        const state = editor.values[slot.id];
        if (!state) return null;
        const common = {
          slot,
          state,
          interfaceLocale,
          issueKey: editor.fieldIssues[slot.id],
          onChange: (next: typeof state) => editor.setField(slot.id, next),
        };
        if (slot.type === 'link') {
          // The CTA label is the only tappable part of the element (PD8 option A).
          const labelId = ctaLabelSlotIdOf(slot.id);
          const labelSlot = editor.slots.find((candidate) => candidate.id === labelId);
          const labelState = labelSlot ? editor.values[labelSlot.id] : undefined;
          const labelMissing =
            labelSlot !== undefined &&
            labelState !== undefined &&
            labelResolvesEmpty(labelSlot, labelState, interfaceLocale);
          return (
            <SlotDestinationField
              key={slot.id}
              {...common}
              client={client}
              storeId={storeId}
              templateId={templateId}
              supabaseUrl={supabaseUrl}
              finishRequested={finishRequested}
              labelMissing={labelMissing}
            />
          );
        }
        if (slot.type === 'rich-text') return <SlotRichTextField key={slot.id} {...common} />;
        return <SlotTextField key={slot.id} {...common} />;
      })}
    </div>
  );
}
